/**
 * Local coin wallet (handegg-roster.md "Pricing"; handegg-accounts-spec.md). Client-only placeholder for the
 * future server-authoritative wallet: not sim content (never hashed into SIM_CONTENT_HASH, never read by the
 * sim) — purely the shop/economy layer, unlocks and coin balance.
 *
 * D1 / RevenueCat (handegg-accounts-spec.md §2, §5, §6): a server build replaces `createLocalWallet` with a
 * `WalletProvider` that calls the server (`GET /me` for the balance, `POST /shop/unlock` to spend, match rewards
 * pushed by the match DO, RevenueCat webhook credits for coin packs) instead of touching local storage — the
 * `WalletProvider` interface below should not need to change, only what implements it.
 */
import { isBallSkinId, isStarterBallSkin, isStarterCharacter, MATCH_REWARDS } from './catalog';
import { isCharacterId } from './characters';

/** Minimal storage shape every host already has (MMKV on mobile, localStorage on web): see
 *  packages/client-core/src/platform.ts `KeyValueStorage`, structurally identical — redeclared here so
 *  `@hf/shared` keeps zero cross-package deps. */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface WalletProvider {
  getBalance(): number;
  /** Add coins (purchase, match reward, grant). Returns the new balance. */
  credit(amount: number, reason: string): number;
  /** Subtract coins if affordable. No-op (returns false) if the balance is insufficient. */
  spend(amount: number, reason: string): boolean;
  /** A sku the wallet already has — true for a starter (free) character/ball skin even before any unlock call. */
  owns(sku: string): boolean;
  /** Spend `priceCoins` and grant `sku`. True (no spend) if already owned or free; false if unaffordable. */
  unlock(sku: string, priceCoins: number): boolean;
}

// ── Pure reward calc (unit-testable without storage) ────────────────────────────────────────────────────────

/** Bot match reward, or 0 if the match was too short or the day's reward cap is already spent. */
export const botMatchReward = (opts: {
  readonly won: boolean;
  readonly durationSec: number;
  /** Bot matches already rewarded today, before this one. */
  readonly rewardedToday: number;
}): number => {
  const R = MATCH_REWARDS.bot;
  if (opts.durationSec < R.minDurationSec) return 0;
  if (opts.rewardedToday >= R.dailyCap) return 0;
  return opts.won ? R.win : R.loss;
};

export interface OnlineMatchRewardOpts {
  readonly won: boolean;
  /** Consecutive wins BEFORE this match (0 right after a loss, or for the first match of a session). Ignored on a loss. */
  readonly priorWinStreak: number;
  /** This win is the first one credited today (the flat daily bonus). Ignored on a loss. */
  readonly firstWinOfDay: boolean;
  /** The win came from an opponent disconnect/forfeit. Ignored on a loss. */
  readonly forfeit: boolean;
}

/**
 * Online match reward (handegg-roster.md): win + streak bonus (+5/consecutive win, capped at +20), halved on a
 * forfeit win. The once-a-day first-win bonus (+50) is added AFTER the halving — it rewards playing (and winning)
 * at all that day, not the quality of this particular win, so a forfeit does not shrink it.
 */
export const onlineMatchReward = (opts: OnlineMatchRewardOpts): number => {
  const R = MATCH_REWARDS.online;
  if (!opts.won) return R.loss;
  const streakBonus = Math.min(opts.priorWinStreak * R.streakBonusPerWin, R.streakBonusMax);
  let reward = R.win + streakBonus;
  if (opts.forfeit) reward = Math.round(reward * R.forfeitWinMul);
  if (opts.firstWinOfDay) reward += R.firstWinOfDayBonus;
  return reward;
};

// ── Local persisted wallet ─────────────────────────────────────────────────────────────────────────────────

interface WalletData {
  balance: number;
  /** Skus actually unlocked with coins (starter/free skus are never stored — `owns` derives those from the catalog). */
  owned: string[];
  /** Device-local day (YYYY-MM-DD) the daily counters below belong to; rolled over lazily on next use. */
  day: string;
  botMatchesRewardedToday: number;
  /** Consecutive online wins (reset to 0 on a loss). Not reset by a new day — only by losing. */
  onlineWinStreak: number;
  onlineFirstWinClaimedToday: boolean;
}

const WALLET_KEY = 'hf.wallet.v1';
const todayKey = (): string => new Date().toISOString().slice(0, 10);

const emptyWallet = (): WalletData => ({
  balance: 0,
  owned: [],
  day: todayKey(),
  botMatchesRewardedToday: 0,
  onlineWinStreak: 0,
  onlineFirstWinClaimedToday: false,
});

const rollDay = (d: WalletData): WalletData => {
  const t = todayKey();
  if (d.day === t) return d;
  return { ...d, day: t, botMatchesRewardedToday: 0, onlineFirstWinClaimedToday: false };
};

const isFreeSku = (sku: string): boolean => {
  if (isCharacterId(sku)) return isStarterCharacter(sku);
  if (isBallSkinId(sku)) return isStarterBallSkin(sku);
  return false;
};

/** Today's reward counters (display only: the Store's "how to earn" panel, Results' "why no coins" line). */
export interface WalletDailyStatus {
  readonly botMatchesRewardedToday: number;
  readonly onlineWinStreak: number;
  readonly onlineFirstWinClaimedToday: boolean;
}

export interface LocalWallet extends WalletProvider {
  /** Today's counters (rolled over to a fresh day first). Read-only: never changes the balance. */
  getDailyStatus(): WalletDailyStatus;
  /** Credits the bot-match reward (0 if the day's cap is spent or the match was too short); advances today's count. */
  rewardBotMatch(won: boolean, durationSec: number): number;
  /** Credits the online-match reward; advances the win streak and the daily first-win flag. */
  rewardOnlineMatch(won: boolean, opts?: { readonly forfeit?: boolean }): number;
  /** Wipe balance / owned / daily counters (account deletion → fresh guest). */
  reset(): void;
}

/** `storage` is typically the host's existing persisted store (mobile: MMKV, web: localStorage) adapted to
 *  `KeyValueStore`. `null` (e.g. storage unavailable) degrades to an in-memory wallet that never persists. */
export const createLocalWallet = (storage: KeyValueStore | null): LocalWallet => {
  let data: WalletData = rollDay(loadWallet(storage));
  const save = (): void => storage?.setItem(WALLET_KEY, JSON.stringify(data));

  const getBalance = (): number => data.balance;

  const credit: WalletProvider['credit'] = (amount) => {
    data.balance += Math.max(0, Math.round(amount));
    save();
    return data.balance;
  };

  const spend: WalletProvider['spend'] = (amount) => {
    const a = Math.max(0, Math.round(amount));
    if (data.balance < a) return false;
    data.balance -= a;
    save();
    return true;
  };

  const owns: WalletProvider['owns'] = (sku) => isFreeSku(sku) || data.owned.includes(sku);

  const unlock: WalletProvider['unlock'] = (sku, priceCoins) => {
    if (owns(sku)) return true;
    if (!spend(priceCoins, `unlock:${sku}`)) return false;
    data.owned.push(sku);
    save();
    return true;
  };

  const rewardBotMatch: LocalWallet['rewardBotMatch'] = (won, durationSec) => {
    data = rollDay(data);
    const amount = botMatchReward({ won, durationSec, rewardedToday: data.botMatchesRewardedToday });
    if (amount <= 0) {
      save();
      return 0;
    }
    data.botMatchesRewardedToday++;
    credit(amount, won ? 'match_reward:bot_win' : 'match_reward:bot_loss');
    return amount;
  };

  const rewardOnlineMatch: LocalWallet['rewardOnlineMatch'] = (won, opts = {}) => {
    data = rollDay(data);
    const firstWinOfDay = won && !data.onlineFirstWinClaimedToday;
    const amount = onlineMatchReward({
      won,
      priorWinStreak: data.onlineWinStreak,
      firstWinOfDay,
      forfeit: opts.forfeit ?? false,
    });
    data.onlineWinStreak = won ? data.onlineWinStreak + 1 : 0;
    if (firstWinOfDay) data.onlineFirstWinClaimedToday = true;
    credit(amount, won ? 'match_reward:online_win' : 'match_reward:online_loss');
    return amount;
  };

  const getDailyStatus: LocalWallet['getDailyStatus'] = () => {
    data = rollDay(data);
    return {
      botMatchesRewardedToday: data.botMatchesRewardedToday,
      onlineWinStreak: data.onlineWinStreak,
      onlineFirstWinClaimedToday: data.onlineFirstWinClaimedToday,
    };
  };

  const reset = (): void => {
    data = emptyWallet();
    save();
  };

  return { getBalance, credit, spend, owns, unlock, rewardBotMatch, rewardOnlineMatch, getDailyStatus, reset };
};

const loadWallet = (storage: KeyValueStore | null): WalletData => {
  const raw = storage?.getItem(WALLET_KEY);
  if (!raw) return emptyWallet();
  try {
    return { ...emptyWallet(), ...(JSON.parse(raw) as Partial<WalletData>) };
  } catch {
    return emptyWallet();
  }
};
